import { CSSProperties, Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import DatePicker, { DatePickerProps, ReactDatePickerCustomHeaderProps } from 'react-datepicker';

import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import timezoneMap from './timezone-short-name-map.json' with { type: 'json' };
import BasicSelectInput from '@ui/select/react-select/basic-select-input';
import { isObject } from 'underscore';
import { DateRange } from './types';
import { getAdjustedDate, getDefaultEnd, getDefaultStart, getUserTimezone } from './utils';
import { toTemporalInstant } from '@js-temporal/polyfill';
import OptionalComponent from '@ui/optional-component/optional-component';

// @ts-expect-error modifying the date prototype to include polyfill
Date.prototype.toTemporalInstant = toTemporalInstant;

/* Don't use css modules, it doesn't work well with react-datepicker */
import './date-range-picker.css';
import { FilterOptionOption } from 'react-select';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type TCustomElementProps = {
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: Dispatch<SetStateAction<Date | null>>;
  setEndDate: Dispatch<SetStateAction<Date | null>>;
};

type DateRangePickerProps = {
  defaultTimezone?: keyof typeof timezoneMap;
  defaultValue?: DateRange;
  /** only pass in value if you want the component to be controlled */
  value?: DateRange;
  onChange?: (data: DateRange) => void;
  datePickerProps?: DatePickerProps;
  timePickerProps?: DatePickerProps;
  Footer?: FunctionComponent<TCustomElementProps> | null;
  Header?: FunctionComponent<TCustomElementProps> | null;
  selectSingleDate?: boolean;
  hideTimezone?: boolean;
  overflowClass?: string;
  borderRadius?: string;
  padding?: string;
  backgroundColor?: string;
  dayMargin?: CSSProperties['margin'];
};

const DateRangePicker = ({
  defaultValue,
  value,
  onChange: propsChange,
  datePickerProps,
  timePickerProps,
  Footer,
  selectSingleDate = false,
  hideTimezone = false,
  overflowClass,
  borderRadius,
  Header,
  padding,
  backgroundColor,
  dayMargin,
}: DateRangePickerProps) => {
  const [startDate, setStartDate] = useState<Date | null>(getDefaultStart(defaultValue, value));
  const [endDate, setEndDate] = useState<Date | null>(getDefaultEnd(defaultValue, value));
  const [invalidEndTime, setInvalidEndTime] = useState<boolean>(false);
  const [selectedTimezone, setSelectedTimezone] = useState<keyof typeof timezoneMap>(
    (defaultValue?.selectedTimezone || getUserTimezone()) as keyof typeof timezoneMap
  );

  useEffect(() => {
    if (value?.startDate && selectSingleDate) {
      setStartDate(new Date(value.startDate));
    } else if (value?.startDate && value.endDate) {
      setStartDate(new Date(value.startDate));
      setEndDate(new Date(value.endDate));
    }
  }, [value, selectSingleDate]);

  const handleDateRangeSelection = (dates: Date[]) => {
    const isDateOrNull = dates.every((date) => date === null || date instanceof Date);
    if (!isDateOrNull) return;

    const [start, end] = dates;

    const newEndDate = end ? new Date(end) : null;
    const startYear = start?.getFullYear() || 0;
    const startMonth = start?.getMonth() || 0;
    const startDay = start?.getDate() || 0;
    const startHour = start?.getHours() || 0;
    const startMinute = start?.getMinutes() || 0;
    const endYear = end?.getFullYear() || 0;
    const endMonth = end?.getMonth() || 0;
    const endDay = end?.getDate() || 0;
    const endHour = end?.getHours() || 0;
    const endMinute = end?.getMinutes() || 0;

    if (
      !value?.endDate &&
      newEndDate &&
      startHour === endHour &&
      startMinute === endMinute &&
      startYear === endYear &&
      startMonth === endMonth &&
      startDay === endDay
    ) {
      // add 5 minutes to the end date
      newEndDate.setMinutes(newEndDate.getMinutes() + 5);
    }

    setStartDate(start);
    setEndDate(newEndDate);

    setInvalidEndTime(!!(newEndDate && start && newEndDate < start));

    propsChange?.({
      startDate: start?.getTime() || null,
      endDate: newEndDate?.getTime() || null,
      selectedTimezone,
    });
  };

  const handleSingleDateSelection = (date: Date | null) => {
    if (!date) return;

    setStartDate(date);

    propsChange?.({
      startDate: date?.getTime() || null,
      endDate: null,
      selectedTimezone,
    });
  };

  return (
    <Container
      borderRadius={borderRadius}
      height="auto"
      background={backgroundColor ?? 'var(--gray-800)'}
      className={'custom-date-range-wrapper'}
      style={
        {
          '--custom-date-range-day-margin-left': dayMargin ?? '-4.5px',
          '--custom-date-range-day-margin-right': dayMargin ?? '-4.5px',
        } as CSSProperties
      }
      padding={padding ?? '16px'}
    >
      {Header ? (
        <Header startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      ) : null}
      <DatePicker
        // this spread is technically correct, but react-datepicker has a poorly written type definition when it comes to user accessibility
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(datePickerProps as any)}
        selected={startDate}
        calendarClassName={`custom-date-picker-calendar ${datePickerProps?.calendarClassName || ''}`}
        renderCustomHeader={(headerProps: ReactDatePickerCustomHeaderProps) => {
          return (
            <div className="flex fd-row jc-center ai-center width-100-percent height-32 margin-b-8 padding-h-4">
              <div>
                <Button
                  className="clear-button square-32 flex fd-row jc-center ai-center"
                  onClick={headerProps.decreaseMonth}
                >
                  <Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={12} />
                </Button>
              </div>
              {/* render short month name from Date */}
              <div className="flex-1 S1">
                <Text color="var(--gray-100)" className="text-center">
                  {headerProps.monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Text>
              </div>
              <div>
                <Button
                  className="clear-button square-32 flex fd-row jc-center ai-center"
                  onClick={headerProps.increaseMonth}
                >
                  <Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={12} />
                </Button>
              </div>
            </div>
          );
        }}
        renderDayContents={(day, date) => {
          const isStart = startDate && date.toDateString() === startDate.toDateString();
          const isEnd = endDate && date.toDateString() === endDate.toDateString();
          const bgGradientProps =
            isStart || isEnd
              ? {
                  gradientOverlay: {
                    height: '100%',
                    background: getThemeColorCSSVariable(),
                  },
                }
              : {};
          const singleDay = startDate && endDate && startDate.toDateString() === endDate.toDateString();
          return (
            <Container
              width="32px"
              height="32px"
              background="var(--gray-800)"
              {...bgGradientProps}
              borderRadius={isStart || isEnd ? '100%' : '0'}
              className={`P2 custom-date-picker-day-content-wrapper overflow-hidden ${singleDay ? 'custom-date-picker-single-day' : ''} ${isStart ? 'custom-date-picker-start-date' : ''} ${isEnd ? 'custom-date-picker-end-date' : ''}`}
            >
              <Text
                color={
                  isStart || isEnd
                    ? getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)
                    : undefined
                }
                className="position-relative height-100-percent width-100-percent flex fd-row jc-center ai-center"
              >
                {day}
              </Text>
            </Container>
          );
        }}
        onChange={selectSingleDate ? handleSingleDateSelection : handleDateRangeSelection}
        startDate={startDate}
        endDate={endDate}
        selectsRange={!selectSingleDate}
        selectsDisabledDaysInRange={datePickerProps?.selectsDisabledDaysInRange ?? false}
        inline={datePickerProps?.inline ?? true}
      />
      <Container
        height="auto"
        background="var(--gray-900)"
        borderRadius="12px"
        className={overflowClass || 'overflow-auto'}
      >
        <Container background="inherit" height="64px" className="flex-row" padding="15px 18px 15px 16px">
          <div className="flex-column flex-1">
            <div className="flex-1 S2 flex ai-center">{selectSingleDate ? '' : 'Start '}Time</div>
            <OptionalComponent display={!hideTimezone}>
              <div style={{ color: 'var(--gray-400)' }} className="flex-1 C3 flex ai-center">
                <BasicSelectInput
                  value={selectedTimezone}
                  placeholder={timezoneMap[selectedTimezone] || 'Los Angeles'}
                  filterOption={(option: FilterOptionOption<unknown>, inputValue) => {
                    const { label } = option;
                    return label.toLowerCase().includes(inputValue.toLowerCase());
                  }}
                  options={Object.entries(timezoneMap)
                    .map((option) => ({
                      label: option[1],
                      value: option[0],
                    }))
                    .toSorted((a, b) => a.label.localeCompare(b.label))}
                  onChange={(selected) => {
                    if (isObject(selected) && 'value' in selected) {
                      setSelectedTimezone(selected.value as keyof typeof timezoneMap);
                      if (startDate && endDate) {
                        propsChange?.({
                          startDate: startDate ? startDate.getTime() : null,
                          endDate: endDate ? endDate.getTime() : null,
                          selectedTimezone: selected.value as string,
                        });
                      }
                      localStorage.setItem('last-timezone', selected.value as string);
                    }
                  }}
                  type="inline"
                />
              </div>
            </OptionalComponent>
          </div>
          <DatePicker
            // this spread is technically correct, but react-datepicker has a poorly written type definition when it comes to user accessibility
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(timePickerProps as any)}
            disabled={!startDate}
            calendarClassName={`custom-time-picker ${timePickerProps?.calendarClassName || ''}`}
            selected={startDate ? new Date(startDate.toLocaleString('en-US', { timeZone: selectedTimezone })) : null}
            onChange={getAdjustedDate(selectedTimezone, (adjustedStartDate: Date | null) => {
              setStartDate(adjustedStartDate);

              // if we are only selecting a single date then no error checking against the end date is possible
              if (selectSingleDate && adjustedStartDate && propsChange) {
                propsChange({
                  startDate: adjustedStartDate.getTime(),
                  endDate: null,
                  selectedTimezone,
                });
                return;
              }

              // user has set a start time but no end time, default to 60 minutes after start time
              let newEndDate = endDate;
              if (adjustedStartDate && !newEndDate) {
                newEndDate = new Date(adjustedStartDate);
                newEndDate.setMinutes(adjustedStartDate.getMinutes() + 60);
                setEndDate(newEndDate);
              }

              const isValid = !!adjustedStartDate && !!newEndDate && adjustedStartDate < newEndDate;
              setInvalidEndTime(!isValid);

              if (isValid && propsChange) {
                propsChange({
                  startDate: adjustedStartDate.getTime(),
                  endDate: newEndDate?.getTime() || null,
                  selectedTimezone,
                });
              }
            })}
            inline={timePickerProps?.inline ?? true}
            showTimeSelectOnly={true}
            timeIntervals={timePickerProps?.timeIntervals ?? 5}
            showTimeInput
          />
        </Container>
        <Container background="var(--gray-800)" height="1px" />
        <OptionalComponent display={!selectSingleDate}>
          <Container background="inherit" height="64px" className="flex-row" padding="15px 18px 15px 16px">
            <div className="flex-1 S2 flex ai-center">End Time</div>
            <DatePicker
              // this spread is technically correct, but react-datepicker has a poorly written type definition when it comes to user accessibility
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(timePickerProps as any)}
              disabled={!endDate}
              calendarClassName={`custom-time-picker ${timePickerProps?.calendarClassName || ''} ${invalidEndTime ? 'custom-time-picker-invalid-time' : ''}`}
              selected={endDate ? new Date(endDate.toLocaleString('en-US', { timeZone: selectedTimezone })) : null}
              onChange={getAdjustedDate(selectedTimezone, (date) => {
                setEndDate(date);

                const isValid = !!date && !!startDate && date > startDate;
                setInvalidEndTime(!isValid);

                if (isValid && propsChange) {
                  propsChange({
                    startDate: startDate?.getTime() || null,
                    endDate: date.getTime(),
                    selectedTimezone,
                  });
                }
              })}
              inline={timePickerProps?.inline ?? true}
              showTimeSelectOnly={true}
              timeIntervals={timePickerProps?.timeIntervals ?? 5}
              showTimeInput
            />
          </Container>
        </OptionalComponent>
      </Container>
      {Footer ? (
        <Footer startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      ) : null}
    </Container>
  );
};

export default DateRangePicker;
